/**
 * Onglet Informations Personnelles - ÉTAPE D
 * Mode lecture par défaut, édition en place
 * Champs sensibles masqués avec bouton afficher (audit trail)
 */

import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
import { 
  Edit2, Save, X, Eye, EyeOff, Loader2, User, MapPin, Phone, CreditCard, UserCircle
} from "lucide-react";
import { DatePickerFr, formatDateFr } from "../../ui/date-picker-fr";
import { FAMILY_STATUS_OPTIONS, getFamilyStatusLabel } from "../../../lib/hr-constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Section component for grouping fields
const Section = ({ title, icon: Icon, children, onEdit, isEditing, onSave, onCancel, isSaving, editDisabled }) => (
  <Card className="border-slate-200">
    <CardHeader className="pb-4">
      <div className="flex items-center justify-between">
        <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
          <Icon className="w-5 h-5 text-blue-600" />
          {title}
        </CardTitle>
        {!isEditing ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={onEdit}
                    disabled={editDisabled}
                    className={`text-slate-500 hover:text-blue-600 ${editDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid={`edit-${title.toLowerCase().replace(/\s+/g, '-')}-btn`}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                </span>
              </TooltipTrigger>
              {editDisabled && (
                <TooltipContent>
                  <p>Enregistrez ou annulez les modifications en cours</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        ) : (
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onCancel}
              disabled={isSaving}
              data-testid={`cancel-${title.toLowerCase().replace(/\s+/g, '-')}-btn`}
            >
              <X className="w-4 h-4 mr-1" />
              Annuler
            </Button>
            <Button 
              size="sm" 
              onClick={onSave}
              disabled={isSaving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid={`save-${title.toLowerCase().replace(/\s+/g, '-')}-btn`}
            >
              {isSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Enregistrer
            </Button>
          </div>
        )}
      </div>
    </CardHeader>
    <CardContent className="pt-0">
      {children}
    </CardContent>
  </Card>
);

// Field display component with ALWAYS visible eye icon for sensitive fields
const Field = ({ label, value, hidden = false, onToggle, isEditing, onChange, type = "text", placeholder, options, disabled, labelFormatter }) => {
  const [visible, setVisible] = useState(false);
  
  // Auto-hide after 30 seconds
  useEffect(() => {
    let timer;
    if (visible && hidden) {
      timer = setTimeout(() => {
        setVisible(false);
      }, 30000); // 30 seconds
    }
    return () => clearTimeout(timer);
  }, [visible, hidden]);
  
  const handleToggle = () => {
    const newVisible = !visible;
    setVisible(newVisible);
    if (newVisible && onToggle) {
      onToggle();
    }
  };
  
  const maskedDisplay = "••••••••••";
  const displayValue = hidden && !visible ? maskedDisplay : (value || "-");
  
  if (isEditing) {
    if (options) {
      return (
        <div className="space-y-1">
          <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</Label>
          <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder={placeholder || "Sélectionner..."} />
            </SelectTrigger>
            <SelectContent>
              {options.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }
    
    // Use DatePicker for date fields
    if (type === "date") {
      return (
        <div className="space-y-1">
          <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</Label>
          <DatePickerFr
            value={value || ""}
            onChange={onChange}
            placeholder="JJ/MM/AAAA"
            disabled={disabled}
          />
        </div>
      );
    }
    
    return (
      <div className="space-y-1">
        <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</Label>
        <Input
          type={type}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="h-9"
          disabled={disabled}
        />
      </div>
    );
  }
  
  // Display formatted value
  let displayFormatted;
  if (type === "date") {
    displayFormatted = formatDateFr(value);
  } else if (labelFormatter && value) {
    displayFormatted = labelFormatter(value);
  } else {
    displayFormatted = displayValue;
  }
  
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
      <div className="flex items-center gap-2">
        <p className={`text-sm text-slate-900 ${hidden ? 'font-mono' : ''}`}>
          {hidden && !visible ? maskedDisplay : displayFormatted}
        </p>
        {/* Eye icon ALWAYS visible for hidden fields (even if no value) */}
        {hidden && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6 text-slate-500 hover:text-blue-600" 
                  onClick={handleToggle}
                  data-testid={`toggle-${label.toLowerCase().replace(/\s+/g, '-')}-btn`}
                  disabled={!value}
                >
                  {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{visible ? "Masquer" : "Afficher"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {hidden && visible && (
          <span className="text-xs text-slate-400">(masqué dans 30s)</span>
        )}
      </div>
    </div>
  );
};

export default function PersonalInfoTab({ employee, onUpdate }) {
  const { token } = useAuth();
  const personal = employee.personal_info || {};
  
  // Edit states for each section
  const [editingSection, setEditingSection] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Form data for editing
  const [formData, setFormData] = useState({});
  
  // Start editing a section
  const startEditing = (section) => {
    setEditingSection(section);
    // Copy current values to form
    setFormData({
      first_name: personal.first_name || "",
      last_name: personal.last_name || "",
      birth_name: personal.birth_name || "",
      birth_date: personal.birth_date || "",
      birth_place: personal.birth_place || "",
      nationality: personal.nationality || "",
      family_status: personal.family_status || "",
      children_count: personal.children_count?.toString() || "0",
      address_street: personal.address?.street || "",
      address_postal_code: personal.address?.postal_code || "",
      address_city: personal.address?.city || "",
      address_country: personal.address?.country || "France",
      phone_personal: personal.phone_personal || "",
      email_personal: personal.email_personal || "",
      email_pro: personal.email_pro || "",
      emergency_name: personal.emergency_contact?.name || "",
      emergency_relationship: personal.emergency_contact?.relationship || "",
      emergency_phone: personal.emergency_contact?.phone || "",
      social_security_number: personal.social_security_number || "",
      iban: personal.bank_details?.iban || "",
      bic: personal.bank_details?.bic || "",
      bank_name: personal.bank_details?.bank_name || "",
    });
  };
  
  // Cancel editing
  const cancelEditing = () => {
    setEditingSection(null);
    setFormData({});
  };
  
  // Update form field
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // Save changes
  const saveChanges = async (section) => {
    setIsSaving(true);
    
    try {
      let payload = { personal_info: {} };
      
      if (section === "identity") {
        payload.personal_info = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          birth_name: formData.birth_name || null,
          birth_date: formData.birth_date || null,
          birth_place: formData.birth_place || null,
          nationality: formData.nationality || null,
          family_status: formData.family_status || null,
          children_count: formData.children_count ? parseInt(formData.children_count) : 0,
        };
      } else if (section === "contact") {
        payload.personal_info = {
          address: {
            street: formData.address_street || null,
            postal_code: formData.address_postal_code || null,
            city: formData.address_city || null,
            country: formData.address_country || "France",
          },
          phone_personal: formData.phone_personal || null,
          email_personal: formData.email_personal || null,
          email_pro: formData.email_pro || null,
        };
      } else if (section === "emergency") {
        payload.personal_info = {
          emergency_contact: {
            name: formData.emergency_name || null,
            relationship: formData.emergency_relationship || null,
            phone: formData.emergency_phone || null,
          },
        };
      } else if (section === "social") {
        payload.personal_info = {
          social_security_number: formData.social_security_number || null,
        };
      } else if (section === "bank") {
        payload.personal_info = {
          bank_details: {
            iban: formData.iban || null,
            bic: formData.bic || null,
            bank_name: formData.bank_name || null,
          },
        };
      }
      
      const response = await axios.put(`${API}/hr/employees/${employee.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      onUpdate(response.data);
      setEditingSection(null);
      
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Erreur", { 
        description: error.response?.data?.detail || "Impossible d'enregistrer les modifications" 
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  // Log sensitive field access to audit trail
  const logFieldAccess = useCallback(async (fieldName) => {
    try {
      await axios.post(`${API}/hr/audit/reveal`, {
        employee_id: employee.id,
        field: fieldName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log(`[AUDIT] Field revealed: ${fieldName} for employee ${employee.id}`);
    } catch (error) {
      // Silent fail - audit logging should not block UI
      console.error("Error logging field access:", error);
    }
  }, [employee.id, token]);
  
  // Check if edit is disabled (another section is being edited)
  const isEditDisabled = (section) => editingSection !== null && editingSection !== section;

  return (
    <div className="space-y-6">
      {/* État civil */}
      <Section 
        title="État civil" 
        icon={User}
        isEditing={editingSection === "identity"}
        onEdit={() => startEditing("identity")}
        onCancel={cancelEditing}
        onSave={() => saveChanges("identity")}
        isSaving={isSaving}
        editDisabled={isEditDisabled("identity")}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field 
            label="Prénom" 
            value={editingSection === "identity" ? formData.first_name : personal.first_name}
            isEditing={editingSection === "identity"}
            onChange={(v) => updateField("first_name", v)}
          />
          <Field 
            label="Nom" 
            value={editingSection === "identity" ? formData.last_name : personal.last_name}
            isEditing={editingSection === "identity"}
            onChange={(v) => updateField("last_name", v)}
          />
          <Field 
            label="Nom de naissance" 
            value={editingSection === "identity" ? formData.birth_name : personal.birth_name}
            isEditing={editingSection === "identity"}
            onChange={(v) => updateField("birth_name", v)}
            placeholder="Si différent"
          />
          <Field 
            label="Date de naissance" 
            value={editingSection === "identity" ? formData.birth_date : personal.birth_date}
            isEditing={editingSection === "identity"}
            onChange={(v) => updateField("birth_date", v)}
            type="date"
            hidden
            onToggle={() => logFieldAccess("birth_date")}
          />
          <Field 
            label="Lieu de naissance" 
            value={editingSection === "identity" ? formData.birth_place : personal.birth_place}
            isEditing={editingSection === "identity"}
            onChange={(v) => updateField("birth_place", v)}
            hidden
            onToggle={() => logFieldAccess("birth_place")}
          />
          <Field 
            label="Nationalité" 
            value={editingSection === "identity" ? formData.nationality : personal.nationality}
            isEditing={editingSection === "identity"}
            onChange={(v) => updateField("nationality", v)}
            hidden
            onToggle={() => logFieldAccess("nationality")}
          />
          <Field 
            label="Situation familiale" 
            value={editingSection === "identity" ? formData.family_status : personal.family_status}
            isEditing={editingSection === "identity"}
            onChange={(v) => updateField("family_status", v)}
            options={FAMILY_STATUS_OPTIONS}
            labelFormatter={getFamilyStatusLabel}
          />
          <Field 
            label="Nombre d'enfants" 
            value={editingSection === "identity" ? formData.children_count : personal.children_count?.toString()}
            isEditing={editingSection === "identity"}
            onChange={(v) => updateField("children_count", v)}
            type="number"
            placeholder="0"
          />
        </div>
      </Section>
      
      {/* Coordonnées */}
      <Section 
        title="Coordonnées" 
        icon={MapPin}
        isEditing={editingSection === "contact"}
        onEdit={() => startEditing("contact")}
        onCancel={cancelEditing}
        onSave={() => saveChanges("contact")}
        isSaving={isSaving}
        editDisabled={isEditDisabled("contact")}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Field 
              label="Adresse" 
              value={editingSection === "contact" ? formData.address_street : personal.address?.street}
              isEditing={editingSection === "contact"}
              onChange={(v) => updateField("address_street", v)}
              placeholder="Numéro et rue"
            />
          </div>
          <Field 
            label="Code postal" 
            value={editingSection === "contact" ? formData.address_postal_code : personal.address?.postal_code}
            isEditing={editingSection === "contact"}
            onChange={(v) => updateField("address_postal_code", v)}
          />
          <Field 
            label="Ville" 
            value={editingSection === "contact" ? formData.address_city : personal.address?.city}
            isEditing={editingSection === "contact"}
            onChange={(v) => updateField("address_city", v)}
          />
          <Field 
            label="Pays" 
            value={editingSection === "contact" ? formData.address_country : (personal.address?.country || "France")}
            isEditing={editingSection === "contact"}
            onChange={(v) => updateField("address_country", v)}
          />
          <div /> {/* Spacer */}
          <Field 
            label="Téléphone personnel" 
            value={editingSection === "contact" ? formData.phone_personal : personal.phone_personal}
            isEditing={editingSection === "contact"}
            onChange={(v) => updateField("phone_personal", v)}
            type="tel"
            hidden
            onToggle={() => logFieldAccess("phone_personal")}
          />
          <Field 
            label="Email personnel" 
            value={editingSection === "contact" ? formData.email_personal : personal.email_personal}
            isEditing={editingSection === "contact"}
            onChange={(v) => updateField("email_personal", v)}
            type="email"
          />
          <Field 
            label="Email professionnel" 
            value={editingSection === "contact" ? formData.email_pro : personal.email_pro}
            isEditing={editingSection === "contact"}
            onChange={(v) => updateField("email_pro", v)}
            type="email"
          />
        </div>
      </Section>
      
      {/* Contact d'urgence */}
      <Section 
        title="Contact d'urgence" 
        icon={Phone}
        isEditing={editingSection === "emergency"}
        onEdit={() => startEditing("emergency")}
        onCancel={cancelEditing}
        onSave={() => saveChanges("emergency")}
        isSaving={isSaving}
        editDisabled={isEditDisabled("emergency")}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field 
            label="Nom" 
            value={editingSection === "emergency" ? formData.emergency_name : personal.emergency_contact?.name}
            isEditing={editingSection === "emergency"}
            onChange={(v) => updateField("emergency_name", v)}
            hidden
            onToggle={() => logFieldAccess("emergency_contact")}
          />
          <Field 
            label="Lien" 
            value={editingSection === "emergency" ? formData.emergency_relationship : personal.emergency_contact?.relationship}
            isEditing={editingSection === "emergency"}
            onChange={(v) => updateField("emergency_relationship", v)}
            placeholder="Conjoint, parent..."
          />
          <Field 
            label="Téléphone" 
            value={editingSection === "emergency" ? formData.emergency_phone : personal.emergency_contact?.phone}
            isEditing={editingSection === "emergency"}
            onChange={(v) => updateField("emergency_phone", v)}
            type="tel"
            hidden
            onToggle={() => logFieldAccess("emergency_phone")}
          />
        </div>
      </Section>
      
      {/* N° Sécurité sociale */}
      <Section 
        title="Sécurité sociale" 
        icon={UserCircle}
        isEditing={editingSection === "social"}
        onEdit={() => startEditing("social")}
        onCancel={cancelEditing}
        onSave={() => saveChanges("social")}
        isSaving={isSaving}
        editDisabled={isEditDisabled("social")}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field 
            label="Numéro de sécurité sociale (NIR)" 
            value={editingSection === "social" ? formData.social_security_number : personal.social_security_number}
            isEditing={editingSection === "social"}
            onChange={(v) => updateField("social_security_number", v)}
            hidden
            onToggle={() => logFieldAccess("social_security_number")}
          />
        </div>
      </Section>
      
      {/* Coordonnées bancaires */}
      <Section 
        title="Coordonnées bancaires" 
        icon={CreditCard}
        isEditing={editingSection === "bank"}
        onEdit={() => startEditing("bank")}
        onCancel={cancelEditing}
        onSave={() => saveChanges("bank")}
        isSaving={isSaving}
        editDisabled={isEditDisabled("bank")}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Field 
              label="IBAN" 
              value={editingSection === "bank" ? formData.iban : personal.bank_details?.iban}
              isEditing={editingSection === "bank"}
              onChange={(v) => updateField("iban", v)}
              hidden
              onToggle={() => logFieldAccess("iban")}
            />
          </div>
          <Field 
            label="BIC" 
            value={editingSection === "bank" ? formData.bic : personal.bank_details?.bic}
            isEditing={editingSection === "bank"}
            onChange={(v) => updateField("bic", v)}
            hidden
            onToggle={() => logFieldAccess("bic")}
          />
          <Field 
            label="Nom de la banque" 
            value={editingSection === "bank" ? formData.bank_name : personal.bank_details?.bank_name}
            isEditing={editingSection === "bank"}
            onChange={(v) => updateField("bank_name", v)}
          />
        </div>
      </Section>
    </div>
  );
}
